import { PerspectiveCamera } from "three";
import { float, mix, perspectiveDepthToViewZ, vec3, vec4 } from "three/tsl";
import { traa } from "three/examples/jsm/tsl/display/TRAANode.js";
import type {
  TslEffect,
  TslEffectContext,
} from "../../../../engine/postprocessing/PostProcessingController";
import type { SsgiOutputMode } from "../../RenderSettingsStore";
import { ssgi } from "./SSGINode.js";

export type SsgiEffectOptions = {
  output?: SsgiOutputMode;
  sliceCount?: number;
  stepCount?: number;
  radius?: number;
  thickness?: number;
  expFactor?: number;
  aoIntensity?: number;
  giIntensity?: number;
  backfaceLighting?: number;
  useTemporalFiltering?: boolean;
  useScreenSpaceSampling?: boolean;
  useLinearThickness?: boolean;
};

function asColorNode(input: unknown): unknown {
  if (
    input &&
    typeof (input as { getTextureNode?: () => unknown }).getTextureNode ===
      "function"
  ) {
    return (input as { getTextureNode: () => unknown }).getTextureNode();
  }
  return input;
}

/**
 * Classic SSGI — three SSGINode (GI.rgb + AO.a). GTAO stays off while this runs.
 */
export class SsgiTslEffect implements TslEffect {
  readonly order = 10;
  readonly branch = "main" as const;
  readonly needsNormal = true;
  readonly needsDiffuse = true;
  get needsVelocity(): boolean {
    return this._useTemporalFiltering;
  }

  private _output: SsgiOutputMode;
  private _sliceCount: number;
  private _stepCount: number;
  private _radius: number;
  private _thickness: number;
  private _expFactor: number;
  private _aoIntensity: number;
  private _giIntensity: number;
  private _backfaceLighting: number;
  private _useTemporalFiltering: boolean;
  private _useScreenSpaceSampling: boolean;
  private _useLinearThickness: boolean;
  private _ssgiNode: ReturnType<typeof ssgi> | undefined;

  constructor(options: SsgiEffectOptions = {}) {
    this._output = options.output ?? "combined";
    this._sliceCount = options.sliceCount ?? 3;
    this._stepCount = options.stepCount ?? 8;
    this._radius = options.radius ?? 12;
    this._thickness = options.thickness ?? 1;
    this._expFactor = options.expFactor ?? 2;
    this._aoIntensity = options.aoIntensity ?? 1;
    this._giIntensity = options.giIntensity ?? 10;
    this._backfaceLighting = options.backfaceLighting ?? 0;
    this._useTemporalFiltering = options.useTemporalFiltering ?? true;
    this._useScreenSpaceSampling = options.useScreenSpaceSampling ?? true;
    this._useLinearThickness = options.useLinearThickness ?? false;
  }

  setParameters(partial: SsgiEffectOptions): boolean {
    let graphDirty = false;
    if (partial.output != null && partial.output !== this._output) {
      this._output = partial.output;
      graphDirty = true;
    }
    if (partial.sliceCount != null) this._sliceCount = partial.sliceCount;
    if (partial.stepCount != null) this._stepCount = partial.stepCount;
    if (partial.radius != null) this._radius = partial.radius;
    if (partial.thickness != null) this._thickness = partial.thickness;
    if (partial.expFactor != null) this._expFactor = partial.expFactor;
    if (partial.aoIntensity != null) this._aoIntensity = partial.aoIntensity;
    if (partial.giIntensity != null) this._giIntensity = partial.giIntensity;
    if (partial.backfaceLighting != null)
      this._backfaceLighting = partial.backfaceLighting;
    if (
      partial.useTemporalFiltering != null &&
      partial.useTemporalFiltering !== this._useTemporalFiltering
    ) {
      this._useTemporalFiltering = partial.useTemporalFiltering;
      graphDirty = true;
    }
    if (partial.useScreenSpaceSampling != null)
      this._useScreenSpaceSampling = partial.useScreenSpaceSampling;
    if (partial.useLinearThickness != null)
      this._useLinearThickness = partial.useLinearThickness;
    this._syncNode();
    return graphDirty;
  }

  private _syncNode(): void {
    const node = this._ssgiNode;
    if (!node) return;
    node.sliceCount.value = this._sliceCount;
    node.stepCount.value = this._stepCount;
    node.radius.value = this._radius;
    node.thickness.value = this._thickness;
    node.expFactor.value = this._expFactor;
    node.aoIntensity.value = this._aoIntensity;
    node.giIntensity.value = this._giIntensity;
    node.backfaceLighting.value = this._backfaceLighting;
    node.useTemporalFiltering = this._useTemporalFiltering;
    node.useScreenSpaceSampling.value = this._useScreenSpaceSampling;
    node.useLinearThickness.value = this._useLinearThickness;
  }

  dispose(): void {
    (this._ssgiNode as { dispose?: () => void } | undefined)?.dispose?.();
    this._ssgiNode = undefined;
  }

  compose(input: unknown, ctx: TslEffectContext): unknown {
    const camera = ctx.camera;
    if (!(camera instanceof PerspectiveCamera)) {
      return input;
    }

    const beauty = asColorNode(input);
    if (this._output === "direct") {
      return beauty;
    }

    if (this._output === "normal") {
      if (!ctx.normal) return beauty;
      const n = asColorNode(ctx.normal) as {
        rgb: { mul: (v: unknown) => { add: (v: unknown) => unknown } };
      };
      return vec4(n.rgb.mul(0.5).add(0.5) as never, 1);
    }

    if (this._output === "depth") {
      if (!ctx.depth) return beauty;
      const raw = (asColorNode(ctx.depth) as { r: unknown }).r;
      const near = float(camera.near);
      const far = float(camera.far);
      const viewZ = perspectiveDepthToViewZ(raw as never, near, far);
      const linear01 = (
        viewZ as {
          negate: () => {
            sub: (n: unknown) => {
              div: (n: unknown) => { saturate: () => { sqrt: () => unknown } };
            };
          };
        }
      )
        .negate()
        .sub(near)
        .div(far.sub(near))
        .saturate();
      const vis = (linear01 as { sqrt: () => { sqrt: () => unknown } })
        .sqrt()
        .sqrt();
      return vec4(vec3(vis as never), 1);
    }

    const normal = (ctx.normal ?? null) as never;
    const node = ssgi(beauty as never, ctx.depth as never, normal, camera);
    this._ssgiNode = node;
    this._syncNode();

    const tex = (
      node as unknown as {
        getTextureNode: () => {
          rgb: { mul: (v: unknown) => unknown };
          a: unknown;
        };
      }
    ).getTextureNode();

    const gi: unknown = tex.rgb;
    const ao: unknown = tex.a;

    if (this._output === "ao") {
      return vec4(vec3(ao as never), 1);
    }
    if (this._output === "gi") {
      return vec4(gi as never, 1);
    }

    const color = beauty as {
      rgb: {
        mul: (v: unknown) => { add: (v: unknown) => unknown };
        add: (v: unknown) => unknown;
      };
      a: unknown;
    };
    const diffuseSrc = asColorNode(ctx.diffuse ?? beauty) as {
      rgb: { mul: (v: unknown) => unknown };
    };

    const litRgb = color.rgb.mul(ao).add(diffuseSrc.rgb.mul(gi));
    const rgb = mix(color.rgb as never, litRgb as never, color.a as never);
    const composite = vec4(rgb as never, color.a as never);

    if (this._useTemporalFiltering && ctx.depth && ctx.velocity) {
      const accumulated = traa(
        composite as never,
        ctx.depth as never,
        ctx.velocity as never,
        camera,
      ) as unknown as { rgb: unknown };
      const accRgb = mix(
        color.rgb as never,
        accumulated.rgb as never,
        color.a as never,
      );
      return vec4(accRgb as never, color.a as never);
    }
    return composite;
  }
}
